import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import Table from "../components/Table";
import ModalImage from "../components/ModalImage";
import bg from "../assets/images/bg-headers.png";
import {
  ExportAuthTableExcelDownload,
  ExportAuthTableExcelQueue,
  formatToRupiahValue,
  generateArrayPages,
} from "../utils/Utils";
import { ReactComponent as ExportIcon } from "../assets/icons/download.svg";
import _ from "lodash";
import { useAuth } from "../components/auth/authContext";
import { requestAuthGet } from "../api/authInstance";
import { useTranslation } from "react-i18next";
import Datepicker from "../components/Datepicker";
import Header from "../components/Header";
import { Tab, Tabs } from "@nextui-org/react";
import StatusColumn from "../components/Column/StatusColumn";
import ImageColumn from "../components/Column/ImageColumn";

export const formatTransactionType = (value) =>
  value
    ? value
        .split("_")
        .map((word) => `${word[0]}${word.slice(1).toLowerCase()}`)
        .join(" ")
    : "-";

export default function TransactionFragment() {
  const { userData } = useAuth();
  const { t } = useTranslation();
  const [date, setDate] = useState({
    startDateLabel: new Date().setDate(new Date().getDate() - 7),
    endDateLabel: new Date(),
    startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });
  const [selected, setSelected] = useState("transaction");
  const [pageSelected, setPageSelected] = useState(1);
  const [filter, setFilter] = useState({
    transactionType: [
      "REDEMPTION",
      "SUBSIDY",
      "PROFIT_SHARING",
      "WITHDRAWAL",
      "DEPOSIT",
      "TRANSFER_DEPOSIT",
      "CASH_REQUEST",
      "ORDER_SETTLEMENT",
    ],
    // voucherStatus: "REDEEMED",
  });

  // * STATE IMAGE MODAL
  const [image, setImage] = useState(null);
  const [showModalImage, setShowModalImage] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [loadingOutlet, setLoadingOutlet] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);

  // RedeemTransactions
  const [redeemTransactions, setRedeemTransactions] = useState({});
  const [outlets, setOutlets] = useState([]);
  const [loadingRedeemTransactions, setLoadingRedeemTransactions] =
    useState(true);

  useEffect(() => {
    userData && GetOutlet();
  }, [userData]);

  useEffect(() => {
    if (userData) {
      GetRedeemTransactions({ isRefresh: true });
      setPageSelected(1);
    }
  }, [filter, date, userData]);

  useEffect(() => {
    if (userData) {
      GetRedeemTransactions({ isRefresh: false });
    }
  }, [pageSelected, userData]);

  const GetRedeemTransactions = async ({ isRefresh, isDownload }) => {
    setLoadingRedeemTransactions(true);
    try {
      const { data } = await requestAuthGet("transaction/wallet", {
        params: {
          ...filter,
          ...date,
          outlet: userData?.role?.walletAdmin
            ? filter.outlet ?? null
            : userData?.outlet?.id ?? null,
          retailer: userData?.outlet?.retailer?.id,
          page: isRefresh ? 1 : pageSelected,
          limit: isDownload ? 999999 : 10,
        },
      });
      if (isDownload) {
        return data.data;
      } else {
        setRedeemTransactions({ ...data });
      }
    } catch (error) {
      console.error("Error ewallet transactions data:", error);
    } finally {
      setLoadingRedeemTransactions(false);
    }
  };

  const GetOutlet = async () => {
    setLoadingOutlet(true);
    try {
      const { data } = await requestAuthGet("outlets", {
        params: {
          retailer: userData?.outlet?.retailer?.id,
          limit: 10000,
        },
      });
      setOutlets(data.data);
    } catch (error) {
      console.error("Error ewallet transactions data:", error);
    } finally {
      setLoadingOutlet(false);
    }
  };

  const options = {
    outlets: [{ label: t("transaction.selectLabel"), value: null }].concat(
      _.map(outlets, (item) => ({
        value: item.id,
        label: item.name,
      })),
    ),
    transactionType:
      selected === "transaction"
        ? [
            {
              label: t("transaction.filterAll"),
              value: [
                "REDEMPTION",
                "SUBSIDY",
                "PROFIT_SHARING",
                "WITHDRAWAL",
                "DEPOSIT",
                "TRANSFER_DEPOSIT",
                "CASH_REQUEST",
                "ORDER_SETTLEMENT",
              ],
            },
            {
              label: t("transaction.filterRedemption"),
              value: "REDEMPTION",
            },
            {
              label: t("transaction.filterSubsidy"),
              value: "SUBSIDY",
            },
            {
              label: "Profit Sharing",
              value: "PROFIT_SHARING",
            },
            {
              label: t("transaction.filterWithdrawal"),
              value: "WITHDRAWAL",
            },
            {
              label: "Deposit",
              value: "DEPOSIT",
            },
            {
              label: "Transfer Deposit",
              value: "TRANSFER_DEPOSIT",
            },
            {
              label: "Cash Request",
              value: "CASH_REQUEST",
            },
            {
              label: "Order Settlement",
              value: "ORDER_SETTLEMENT",
            },
          ]
        : [
            {
              label: t("transaction.filterAll"),
              value: [
                "REDEMPTION_VALID",
                "SUBSIDY",
                "PROFIT_SHARING",
                "WITHDRAWAL",
              ],
            },
            {
              label: t("transaction.filterApproved"),
              value: "REDEMPTION_VALID",
            },
            {
              label: t("transaction.filterSubsidy"),
              value: "SUBSIDY",
            },
            {
              label: "Profit Sharing",
              value: "PROFIT_SHARING",
            },
            {
              label: t("transaction.filterWithdrawal"),
              value: "WITHDRAWAL",
            },
            // {
            //   label: "Cash Request",
            //   value: "CASH_REQUEST",
            // },
          ],
    status: [
      {
        label: "REDEEMED",
        value: "REDEEMED",
      },
      {
        label: "ACTIVATED",
        value: "ACTIVATED",
      },
      {
        label: "OPEN",
        value: "OPEN",
      },
      {
        label: "BOOKED",
        value: "BOOKED",
      },
    ],
  };

  const columns = {
    transaction: useMemo(() => {
      const dynamicColumn = [
        {
          Header: "No.",
          Cell: ({ row }) => {
            const startIndex = (pageSelected - 1) * 10;
            return (
              <div className="text-center">{startIndex + row.index + 1}</div>
            );
          },
        },

        {
          Header: t("transaction.timeStamp"),
          accessor: "createdAt",
          mapping: (data) => moment(data).format("DD-MM-YYYY HH:mm"),
          Cell: ({ row }) => {
            const date = row.original?.createdAt ?? null;
            if (date) {
              return (
                <div className="text-center">
                  {date !== null
                    ? moment.utc(date).local().format("DD-MM-YYYY HH:mm")
                    : "-"}
                </div>
              );
            } else {
              return <div className="text-center">-</div>;
            }
          },
        },
        {
          Header: t("transaction.type"),
          accessor: "transactionType",
          Cell: ({ row }) => (
            <div className="text-center">
              <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                {formatTransactionType(row.original.transactionType)}
              </span>
            </div>
          ),
        },
        selected === "transaction" && {
          Header: t("transaction.outletName"),
          accessor: "outletName",
          Cell: ({ row }) => (
            <div className="text-center">{row.original.outletName ?? "-"}</div>
          ),
        },
        selected === "transaction" && {
          Header: t("transaction.phoneNumber"),
          accessor: "phone",
          mapping: (data) =>
            !_.isEmpty(data)
              ? `+${data.substring(0, data.length - 4)}XXXX`
              : "",
          Cell: ({ row }) => {
            const phone = row.original.phone ?? "";
            return (
              <div className="text-center">
                {phone.length > 0
                  ? `+${phone.substring(0, phone.length - 4)}XXXX`
                  : "-"}
              </div>
            );
          },
        },
        selected === "transaction" && {
          Header: t("transaction.nominal"),
          accessor: "nominal",
          Cell: ({ value }) => (
            <div className="text-center">
              {value ? formatToRupiahValue(value) : "-"}
            </div>
          ),
        },
        selected === "transaction" && {
          Header: t("transaction.voucher"),
          accessor: "uvc",
          Cell: ({ row }) => (
            <div className="text-center">{row.original?.uvc ?? "-"}</div>
          ),
        },
        selected === "transaction" && {
          Header: "SKU",
          accessor: "skus",
          mapping: (data) =>
            !_.isEmpty(data) ? data.map((sku) => sku.name).toString() : "-",
          Cell: ({ row }) => {
            const skuNames = !_.isEmpty(row.original.skus)
              ? row.original.skus.map((sku) => sku.name).toString()
              : "-";
            const truncatedText =
              skuNames.length > 30
                ? `${skuNames.substring(0, 30)}...`
                : skuNames;
            return <div className="text-center">{truncatedText}</div>;
          },
        },
        selected === "transaction" && {
          Header: t("transaction.status"),
          accessor: "voucherStatus",
          Cell: ({ row }) => {
            const value =
              row.original.voucherStatus ?? row.original.status ?? "-";
            const validatorStatus = row.original.validatorStatus;
            return (
              <StatusColumn
                getValue={() =>
                  validatorStatus !== "REJECT" ? value : "REJECT"
                }
              />
            );
          },
        },
        selected === "transaction" && {
          Header: "Receipt Image",
          accessor: "receiptImageUrl",
          Cell: ({ row }) => (
            <ImageColumn
              image={row.original.receiptImageUrl}
              setImage={setImage}
              setShowModalImage={setShowModalImage}
            />
          ),
        },
        selected === "transaction" && {
          Header: "Kartini Validation",
          accessor: "validatorStatus",
          Cell: ({ row }) => (
            <StatusColumn
              getValue={() => _.get(row.original, `validatorStatus`, "PENDING")}
            />
          ),
        },
        selected === "transaction" && {
          Header: "Description",
          accessor: "validatorDescription",
          Cell: ({ row }) => {
            const value = row.original.validatorDescription ?? "-";
            return <div className="text-center">{value}</div>;
          },
        },
        selected === "transaction" && {
          Header: "Client Approval",
          accessor: "clientStatus",
          Cell: ({ row }) => (
            <StatusColumn
              getValue={() => {
                const clientStatus = _.get(
                  row.original,
                  `clientStatus`,
                  "PENDING",
                );
                return clientStatus == null ? "PENDING" : clientStatus;
              }}
            />
          ),
        },

        selected === "subsidy" && {
          Header: t("transaction.nominal"),
          accessor: "nominal",
          Cell: ({ value }) => (
            <div className="text-center">
              {value ? formatToRupiahValue(value) : "-"}
            </div>
          ),
        },
        selected === "subsidy" && {
          Header: "Status",
          mapping: (data) =>
            data?.transactionType === "REDEMPTION"
              ? data?.clientStatus ?? data?.validatorStatus
              : data?.status,
          Cell: ({ row }) => {
            const value =
              row.original?.transactionType === "REDEMPTION"
                ? row.original?.clientStatus ?? row.original?.validatorStatus
                : row.original?.status;

            return (
              <div className="m-1.5 text-center">
                <div
                  className={`inline-flex text-xs font-medium ${
                    value === "REJECT"
                      ? "bg-red-200 text-red-600"
                      : value === "ACCEPT" || value === "APPROVED"
                        ? "bg-green-100 text-green-600"
                        : "bg-blue-100 text-blue-600"
                  } rounded-full px-2.5 py-1 text-center`}
                >
                  {value}
                </div>
                {/* End */}
              </div>
            );
          },
        },
        {
          Header: t("transaction.detail"),
          headerClassName: "text-start",
          accessor: "description",
          Cell: ({ value }) => <div className="text-start">{value ?? "-"}</div>,
        },
      ].filter(Boolean);

      return dynamicColumn;
    }, [selected, pageSelected]),
  };

  const handleChange = (name, event) => {
    setFilter((prevState) => ({
      ...prevState,
      [name]: event.value,
    }));
  };

  return (
    <>
      <ModalImage
        modalOpen={showModalImage}
        setModalOpen={setShowModalImage}
        image={image}
      />
      <Header
        title="Riwayat Transaksi"
        transparent
        rightButtonIcon={
          userData?.outlet?.retailer?.id ? (
            !loadingDownload ? (
              <ExportIcon />
            ) : (
              <svg
                className="h-6 w-6 animate-spin text-gray-500"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="3"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            )
          ) : null
        }
        rightButton={async () => {
          if (loadingDownload) return;
          setLoadingDownload(true);

          await ExportAuthTableExcelDownload(
            `transaction/file/export?exportType=${selected === "subsidy" ? "SUBSIDYWITHDRAWAL" : "TRANSACTION"}`,
            {
              ...filter,
              ...date,
              retailer: userData?.outlet?.retailer?.id,
            },
            "Transaction",
          )
            .then(() => {
              setLoadingDownload(false);
            })
            .catch((err) => {
              setLoadingDownload(false);
            });

          setLoadingDownload(false);
        }}
        disabledRightButton={_.isEmpty(redeemTransactions.data)}
      />
      <img src={bg} alt="" className="absolute top-0 -z-50 -mt-5 w-full" />
      <div className="mx-auto max-w-screen-xl pb-24 pt-24">
        <div className="space-y-3">
          {/* MODAL IMAGE */}
          <ModalImage
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            image={image}
            setImage={setImage}
          />
          <div className="w-full px-4">
            <Tabs
              fullWidth
              size="sm"
              aria-label="Tabs form"
              items={[
                { id: "transaction", label: t("transaction.tabTransaction") },
                { id: "subsidy", label: t("transaction.tabSubsidy") },
              ]}
              selectedKey={selected}
              onSelectionChange={(key) => {
                setSelected(key);

                const newTransactionType =
                  key === "transaction"
                    ? [
                        "REDEMPTION",
                        "SUBSIDY",
                        "PROFIT_SHARING",
                        "WITHDRAWAL",
                        "DEPOSIT",
                        "TRANSFER_DEPOSIT",
                        "CASH_REQUEST",
                        "ORDER_SETTLEMENT",
                      ]
                    : [
                        "REDEMPTION_VALID",
                        "SUBSIDY",
                        "PROFIT_SHARING",
                        "WITHDRAWAL",
                      ];

                setFilter((prevFilter) => {
                  if (
                    prevFilter.transactionType?.toString() !==
                    newTransactionType.toString()
                  ) {
                    return {
                      ...prevFilter,
                      transactionType: newTransactionType,
                    };
                  }
                  return prevFilter;
                });
              }}
              classNames={{
                tabList: "bg-transparent-black-10 rounded-lg p-1.5",
                cursor: "!bg-monochrome-0 rounded-md",
                tab: "py-5 text-monochrome-600 font-semibold",
              }}
            >
              {(item) => (
                <Tab
                  key={item.id}
                  title={
                    <div className="flex items-center space-x-2">
                      <span>{item.label}</span>
                    </div>
                  }
                  className="px-0 focus:outline-none"
                ></Tab>
              )}
            </Tabs>
          </div>
          <div className="px-4">
            {userData?.role?.walletAdmin && (
              <Select
                className="col-span-full w-full sm:col-span-2"
                placeholder="Outlet Name"
                components={{ IndicatorSeparator: () => null }}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "0.5rem 0.5rem 0 0",
                    borderColor: "#e6e6e6",
                    padding: "0.125rem 0.125rem",
                  }),
                }}
                isLoading={loadingOutlet}
                options={options.outlets}
                onChange={(e) => handleChange("outlet", e)}
                value={_.find(options.outlets, {
                  value: filter.outlet,
                })}
              />
            )}
            <Datepicker
              align="right"
              setDate={setDate}
              date={date}
              size="w-full"
            />
            <Select
              className="col-span-full w-full sm:col-span-2"
              placeholder="Tipe Transaksi"
              components={{ IndicatorSeparator: () => null }}
              styles={{
                control: (base) => ({
                  ...base,
                  borderRadius: "0 0 0.5rem 0.5rem",
                  borderColor: "#e6e6e6",
                  padding: "0.125rem 0.125rem",
                }),
              }}
              options={options.transactionType}
              onChange={(e) => handleChange("transactionType", e)}
              value={_.find(options.transactionType, {
                value: filter.transactionType,
              })}
            />
          </div>
          <div className="space-y-5">
            <Table
              data={redeemTransactions.data ?? []}
              pages={generateArrayPages(redeemTransactions.pageCount ?? 0)}
              setPageSelected={setPageSelected}
              pageSelected={pageSelected ? pageSelected - 1 : 0}
              title={t("transaction.title")}
              columns={columns.transaction}
              isLoading={loadingRedeemTransactions}
              date={date}
              setDate={setDate}
            />
          </div>
        </div>
      </div>
    </>
  );
}
